import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { toast } from "react-toastify"
import Table from "../../../core/common/dataTable/index"
import { buildUploadForm } from "../../../service/certificate"

export type FieldType = "text" | "textarea" | "select"

export interface FormField {
  name: string
  label: string
  type?: FieldType
  required?: boolean
  options?: { value: string; label: string }[]
  placeholder?: string
}

export interface ToolbarFilter {
  name: string           // query param sent to list()
  placeholder: string    // shown as "All …" option
  options: { value: string; label: string }[]
}

export interface MasterDataConfig {
  title: string
  subtitle?: string
  // CRUD api (from service/certificate) — list/create/update/remove.
  api: {
    list: (p?: any) => Promise<{ success: boolean; data: any[] }>
    create: (d: any) => Promise<any>
    update: (id: string, d: any) => Promise<any>
    remove: (id: string) => Promise<any>
    restore?: (id: string) => Promise<any>
  }
  columns: { title: string; dataIndex: string; render?: (v: any, r: any) => any }[]
  fields: FormField[]
  // Optional dropdown filters shown in the list toolbar (e.g. category filter).
  // Each filter value is passed as a query param to list().
  toolbarFilters?: ToolbarFilter[]
  // Default form values for "Add new" (e.g. { category: "Academic" }).
  defaultValues?: Record<string, any>
  // When set, the create/update payload is sent as multipart FormData
  // with the file under this field name ("image" | "file").
  uploadField?: "image" | "file"
  uploadLabel?: string
  uploadRequiredOnCreate?: boolean
}

const MasterDataPage = ({ config }: { config: MasterDataConfig }) => {
  const [items, setItems] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [showModal, setShowModal] = useState(false)
  const [editing, setEditing] = useState<any | null>(null)
  const [form, setForm] = useState<Record<string, any>>({})
  const [file, setFile] = useState<File | null>(null)
  const [saving, setSaving] = useState(false)
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [showInactive, setShowInactive] = useState(false)
  // One state entry per toolbar filter (keyed by filter.name)
  const [filterValues, setFilterValues] = useState<Record<string, string>>(() =>
    Object.fromEntries((config.toolbarFilters || []).map(f => [f.name, ""]))
  )

  const fetchItems = async () => {
    setLoading(true)
    try {
      const params: Record<string, any> = {}
      if (showInactive) params.includeInactive = 1
      Object.entries(filterValues).forEach(([k, v]) => { if (v) params[k] = v })
      const res = await config.api.list(Object.keys(params).length ? params : undefined)
      if (res.success) setItems(res.data)
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to load")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchItems()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showInactive, JSON.stringify(filterValues)])

  // Reset to page 1 whenever the data set changes.
  useEffect(() => { setPage(1) }, [items.length])

  const handleRestore = async (id: string) => {
    if (!config.api.restore) return
    try {
      await config.api.restore(id)
      toast.success("Restored successfully")
      fetchItems()
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Restore failed")
    }
  }

  const openAdd = () => {
    setEditing(null)
    setForm({ ...(config.defaultValues || {}) })
    setFile(null)
    setShowModal(true)
  }
  const openEdit = (item: any) => {
    setEditing(item)
    const f: Record<string, any> = {}
    config.fields.forEach(fl => (f[fl.name] = item[fl.name] ?? ""))
    setForm(f)
    setFile(null)
    setShowModal(true)
  }
  const closeModal = () => {
    setShowModal(false)
    setEditing(null)
    setForm({})
    setFile(null)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    // Required validation
    for (const fl of config.fields) {
      if (fl.required && !String(form[fl.name] ?? "").trim()) {
        toast.error(`${fl.label} is required.`)
        return
      }
    }
    if (config.uploadField && config.uploadRequiredOnCreate && !editing && !file) {
      toast.error(`${config.uploadLabel || "File"} is required.`)
      return
    }
    setSaving(true)
    try {
      let payload: any
      if (config.uploadField) {
        payload = buildUploadForm(form, file, config.uploadField)
      } else {
        payload = form
      }
      if (editing) await config.api.update(editing._id, payload)
      else await config.api.create(payload)
      toast.success(editing ? "Updated successfully" : "Created successfully")
      closeModal()
      fetchItems()
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Save failed")
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!deleteId) return
    try {
      await config.api.remove(deleteId)
      toast.success("Deleted successfully")
      fetchItems()
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Delete failed")
    } finally {
      setDeleteId(null)
    }
  }

  const columns = [
    ...config.columns,
    {
      title: "Status",
      dataIndex: "isActive",
      render: (v: boolean) =>
        v === false ? (
          <span className="badge bg-secondary-transparent text-secondary">Inactive</span>
        ) : (
          <span className="badge bg-success-transparent text-success">Active</span>
        ),
    },
    {
      title: "Action",
      dataIndex: "action",
      render: (_: any, record: any) =>
        record.isActive === false ? (
          <button className="btn btn-sm btn-outline-success" onClick={() => handleRestore(record._id)}>
            <i className="ti ti-restore me-1" />Restore
          </button>
        ) : (
          <div className="d-flex align-items-center gap-2">
            <Link to="#" className="btn btn-sm btn-outline-primary" onClick={() => openEdit(record)}>
              <i className="ti ti-edit" />
            </Link>
            <Link
              to="#"
              className="btn btn-sm btn-outline-danger"
              onClick={() => setDeleteId(record._id)}
            >
              <i className="ti ti-trash" />
            </Link>
          </div>
        ),
    },
  ]

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">{config.title}</h3>
            {config.subtitle && <p className="text-muted mb-0">{config.subtitle}</p>}
          </div>
          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap gap-2">
            {(config.toolbarFilters || []).map(tf => (
              <select
                key={tf.name}
                className="form-select"
                style={{ maxWidth: 180 }}
                value={filterValues[tf.name] || ""}
                onChange={e => setFilterValues(prev => ({ ...prev, [tf.name]: e.target.value }))}
              >
                <option value="">{tf.placeholder}</option>
                {tf.options.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            ))}
            {config.api.restore && (
              <div className="form-check form-switch mb-0 me-2">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="cert_md_show_inactive"
                  checked={showInactive}
                  onChange={e => setShowInactive(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="cert_md_show_inactive">Show inactive</label>
              </div>
            )}
            <button className="btn btn-primary" onClick={openAdd}>
              <i className="ti ti-square-rounded-plus me-2" />
              Add {config.title.replace(/s$/, "")}
            </button>
          </div>
        </div>

        <div className="card">
          <div className="card-body p-0 py-3">
            <Table
              dataSource={items}
              columns={columns}
              loading={loading}
              pagination={{
                currentPage: page,
                limit: pageSize,
                total: items.length,
                onChange: (p: number, ps: number) => { setPage(p); setPageSize(ps) },
              }}
            />
          </div>
        </div>
      </div>

      {/* Add / Edit modal */}
      {showModal && (
        <>
          <div className="modal show d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h4 className="modal-title">
                    {editing ? "Edit" : "Add"} {config.title.replace(/s$/, "")}
                  </h4>
                  <button type="button" className="btn-close" onClick={closeModal} />
                </div>
                <form onSubmit={handleSubmit}>
                  <div className="modal-body">
                    {config.fields.map(fl => (
                      <div className="mb-3" key={fl.name}>
                        <label className="form-label">
                          {fl.label}
                          {fl.required && " *"}
                        </label>
                        {fl.type === "textarea" ? (
                          <textarea
                            className="form-control"
                            rows={3}
                            placeholder={fl.placeholder}
                            value={form[fl.name] ?? ""}
                            onChange={e => setForm({ ...form, [fl.name]: e.target.value })}
                          />
                        ) : fl.type === "select" ? (
                          <select
                            className="form-select"
                            value={form[fl.name] ?? ""}
                            onChange={e => setForm({ ...form, [fl.name]: e.target.value })}
                          >
                            <option value="">Select...</option>
                            {(fl.options || []).map(o => (
                              <option key={o.value} value={o.value}>
                                {o.label}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <input
                            type="text"
                            className="form-control"
                            placeholder={fl.placeholder}
                            value={form[fl.name] ?? ""}
                            onChange={e => setForm({ ...form, [fl.name]: e.target.value })}
                          />
                        )}
                      </div>
                    ))}
                    {config.uploadField && (
                      <div className="mb-3">
                        <label className="form-label">
                          {config.uploadLabel || "File"}
                          {config.uploadRequiredOnCreate && !editing && " *"}
                        </label>
                        <input
                          type="file"
                          className="form-control"
                          accept={config.uploadField === "image" ? "image/*" : ".woff2,.woff,.ttf,.otf"}
                          onChange={e => setFile(e.target.files?.[0] || null)}
                        />
                        {editing && (
                          <small className="text-muted">Leave empty to keep the current file.</small>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-light" onClick={closeModal} disabled={saving}>
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={saving}>
                      {saving ? "Saving..." : editing ? "Update" : "Create"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}

      {/* Delete confirmation — React-controlled, no Bootstrap JS required */}
      {deleteId && (
        <>
          <div className="modal show d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-body text-center p-4">
                  <span className="text-danger" style={{ fontSize: 40 }}>
                    <i className="ti ti-trash-x" />
                  </span>
                  <h4 className="mt-2">Confirm Delete</h4>
                  <p className="text-muted">This soft-deletes the record — it can be restored later.</p>
                  <div className="d-flex justify-content-center gap-2">
                    <button className="btn btn-light" onClick={() => setDeleteId(null)}>Cancel</button>
                    <button className="btn btn-danger" onClick={handleDelete}>Yes, Delete</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}
    </div>
  )
}

export default MasterDataPage
